import { parse } from 'date-fns';
import { makeDateConsistent } from 'helpers/dates';
import { uniqBy } from 'lodash';
import {
    DeviceConsumptionResult,
    DevicePrediction,
    DeviceReadingsResult,
    ProcessedPrediction,
} from 'types';

const decodeSig = (sig: number) => {
    const binaryArray = Array.from(Array(32).keys()).reverse();
    let binaryString = '';
    for (const i in binaryArray) binaryString += 1 & (sig >> binaryArray[i]);
    return binaryString;
};

export const sortSeries = (input: number[][]): number[][] => {
    const compareFn = (a: number[], b: number[]) => (a[0] > b[0] ? 1 : -1);
    input.sort(compareFn);
    return uniqBy(input, (value) => value[0]);
};

export const sendNowReading = (sig: number) => {
    const binaryString = decodeSig(sig);
    if (binaryString[binaryString.length - 9] === '1') return true;
    return false;
};

export const thresholdTriggeredReading = (sig: number) => {
    const binaryString = decodeSig(sig);
    const length = binaryString.length;
    if (binaryString[length - 7] === '1') return true;
    if (binaryString[length - 12] === '1') return true;
    return false;
};

export const covReading = (sig: number) => {
    const binaryString = decodeSig(sig);
    const length = binaryString.length;
    if (binaryString[length - 5] === '1') return true;
    if (binaryString[length - 6] === '1') return true;
    return false;
};

export const buttonPressReading = (sig: number) => {
    const binaryString = decodeSig(sig);
    if (binaryString[binaryString.length - 1] === '1') return true;
    return false;
};

export const getLastPredictionDate = (
    lastReading: number,
    lastConsumption: number,
    consumption: DeviceConsumptionResult
) => {
    let output = 0;
    for (const readingId in consumption) {
        const reading = consumption[readingId];
        if (!!reading.predictions) {
            for (const date in reading.predictions) {
                const processedPrediction = getDataFromPrediction(
                    reading.predictions[date],
                    lastConsumption,
                    lastReading
                );
                if (processedPrediction.date > output)
                    output = processedPrediction.date;
            }
        }
    }
    return output;
};
export const convertConsumptionTime = (inputTime: string) => {
    const convertedString =
        inputTime.substring(0, 4) +
        '-' +
        inputTime.substring(4, 6) +
        '-' +
        inputTime.substring(6, 8);
    const outputTime = Date.parse(convertedString);
    return outputTime;
};

export const getDataFromPrediction = (
    prediction: DevicePrediction,
    lastConsumption: number,
    lastReading: number
): ProcessedPrediction => {
    const output: ProcessedPrediction = {
        date: 0,
        consumption: null,
        hdd: null,
        tank: null,
    };
    output.date = Number(makeDateConsistent(prediction.datetime));
    const date = Number(
        parse(prediction.dd.toString(), 'yyyyMMdd', new Date())
    );
    if (date > lastConsumption) {
        const consumptionValue =
            prediction.consumption || prediction.consumption === '0.0'
                ? prediction.consumption
                : prediction.consumptionCAvg7;
        output.consumption = Number(consumptionValue);
        output.hdd = prediction.hdd;
    }
    if (output.date > lastReading) {
        const tankValue = prediction.tank || prediction.tankCAvg7;
        output.tank = Number(tankValue);
    }
    return output;
};

export const getLastReading = (readings: DeviceReadingsResult): number => {
    let output = 0;
    for (const reading of readings.data)
        if (!reading.hide) output = reading.time;
    return output;
};

export const getLastConsumption = (
    consumption: DeviceConsumptionResult
): number => {
    let output = 0;
    for (const readingId in consumption) {
        const reading = consumption[readingId];
        if (!!reading.consumption || reading.consumption === 0) {
            output = convertConsumptionTime(readingId);
        }
    }
    return output;
};
